import React, { useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Phone, CheckCircle2, ArrowRight, ShieldCheck, MapPin, AlertCircle, Clock } from "lucide-react";
import Layout from "../components/Layout";
import Breadcrumbs from "../components/Breadcrumbs";
import QuoteForm from "../components/QuoteForm";
import HeroBackground from "../components/HeroBackground";
import PreFooterCTA from "../components/PreFooterCTA";
import FaqList from "../components/FaqList";
import { SERVICES, SERVICE_AREAS, BUSINESS, SITE_URL, getServiceFaqs, getServiceContent } from "../data/business";

const RELATED_SERVICE_COUNT = 3;
const NEARBY_AREA_COUNT = 8;
const SIGNS_LIST = [
  "Water where it shouldn't be",
  "Unusual sounds or smells",
  "Slow drainage or low pressure",
  "Higher-than-normal water bill",
  "Visible rust, drips, or corrosion",
  "Repairs that keep coming back",
];
const WHY_LIST = ["Local Baltimore plumbing contractor", "Upfront, written estimates", "Clean, careful job site", "Licensed & insured"];
const PROCESS_STEPS = [
  "Call us or request a free quote online.",
  "A licensed technician inspects the line.",
  "You get a clear, upfront estimate.",
  "We do the work — clean, careful, and on time.",
];

function buildServiceFaqs(service) {
  return getServiceFaqs(service.slug);
}

function ServiceHero({ service, breadcrumbs }) {
  return (
    // Match Home hero: fills viewport above the fold (chrome ~100.5px at lg+).
    <section className="relative isolate overflow-hidden text-white lg:min-h-[calc(100vh-100px)] lg:flex lg:items-center">
      <HeroBackground />
      <div className="abc-container w-full py-8 lg:py-12 grid lg:grid-cols-12 gap-6 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <Breadcrumbs items={breadcrumbs} />
          <div className="inline-flex items-center gap-2 mt-4 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-[11px] sm:text-xs font-semibold uppercase tracking-widest">
            <ShieldCheck className="w-3.5 h-3.5 text-[#E31D2B]" /> {service.category}
          </div>
          <h1 className="font-display mt-3 text-[28px] leading-[1.1] sm:text-4xl lg:text-5xl font-extrabold sm:leading-tight" data-testid="service-h1">
            {service.name} in the Baltimore &amp; DC Metros
          </h1>
          {/* Short on mobile, full on desktop */}
          <p className="mt-3 sm:hidden text-[15px] text-white/85 leading-relaxed">{service.short}</p>
          <p className="mt-4 hidden sm:block text-white/85 max-w-xl text-base md:text-lg">{service.short} Upfront pricing, clean work, and a local team you can talk to.</p>
          <div className="mt-5 flex flex-wrap gap-3">
            <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent" data-testid="service-call-cta">
              <Phone className="w-5 h-5" /> Call {BUSINESS.phone}
            </a>
            <Link to="/contact" className="abc-btn abc-btn-ghost-light" data-testid="service-quote-cta">Get a Free Quote <ArrowRight className="w-5 h-5" /></Link>
          </div>
          <div className="mt-4 text-[13px] sm:text-sm text-white/75 flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#E31D2B]" /> {BUSINESS.hours.weekdays}</span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-4 h-4 text-[#54616E]" />
              <span className="sm:hidden">Baltimore &amp; the metro</span>
              <span className="hidden sm:inline">Baltimore &amp; nearby metro communities</span>
            </span>
          </div>
        </div>
        <div className="lg:col-span-5">
          <QuoteForm source={`service-${service.slug}`} />
        </div>
      </div>
    </section>
  );
}

function ServiceOverview({ service }) {
  const lowerName = service.name.toLowerCase();
  const content = getServiceContent(service.slug);
  const whatIs = content?.whatIs || [
    `${service.name} covers the diagnosis, cleaning, repair, and replacement of the drain and sewer lines this work involves. Our team uses modern tools — cameras, jetting, and proper-size equipment — so the work lasts.`,
    "Whether it's a quick clear or a full project, you'll get a clear estimate first and a clean job site after.",
  ];
  const signs = content?.signs || SIGNS_LIST;
  const why = content?.why || WHY_LIST;
  return (
    <>
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">What is {lowerName}?</h2>
        {whatIs.map((p, i) => (
          <p key={i} className="mt-3 text-slate-600 leading-relaxed" data-testid={`service-whatis-${i}`}>{p}</p>
        ))}
      </div>

      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">Signs you need {lowerName}</h2>
        <ul className="mt-4 grid sm:grid-cols-2 gap-3">
          {signs.map((s) => (
            <li key={s} className="flex items-start gap-2 text-slate-700">
              <AlertCircle className="w-5 h-5 text-[#54616E] flex-shrink-0 mt-0.5" /> {s}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">Why choose Drain Sights for {lowerName}</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          {why.map((p) => (
            <div key={p} className="flex items-start gap-2"><CheckCircle2 className="w-5 h-5 text-[#54616E] mt-0.5" /> <span className="text-slate-700">{p}</span></div>
          ))}
        </div>
      </div>

      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">Our process</h2>
        <ol className="mt-4 space-y-3">
          {PROCESS_STEPS.map((step, i) => (
            <li key={step} className="flex gap-3"><span className="w-7 h-7 rounded-lg bg-[#1A1D21] text-white inline-flex items-center justify-center font-bold text-sm flex-shrink-0">{i + 1}</span><span className="text-slate-700 pt-1">{step}</span></li>
          ))}
        </ol>
      </div>
    </>
  );
}

function ServiceSidebar({ related }) {
  return (
    <aside className="lg:col-span-4 space-y-6">
      <div className="abc-card p-6">
        <h3 className="font-display font-bold text-[#1A1D21]">Related services</h3>
        <ul className="mt-3 space-y-2">
          {related.map((r) => (
            <li key={r.slug}><Link to={`/services/${r.slug}`} className="text-[#54616E] hover:underline" data-testid={`related-${r.slug}`}>{r.name} →</Link></li>
          ))}
        </ul>
      </div>
      <div className="abc-card p-6">
        <h3 className="font-display font-bold text-[#1A1D21] flex items-center gap-2"><MapPin className="w-4 h-4 text-[#54616E]" /> We serve nearby</h3>
        <ul className="mt-3 grid grid-cols-2 gap-1.5 text-sm">
          {SERVICE_AREAS.slice(0, NEARBY_AREA_COUNT).map((a) => (
            <li key={a.slug}><Link to={`/service-areas/${a.slug}`} className="text-slate-700 hover:text-[#54616E]" data-testid={`service-area-${a.slug}`}>{a.city}</Link></li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-[#1A1D21] to-[#262A30] text-white p-6">
        <h3 className="font-display font-bold text-lg">Ready to schedule?</h3>
        <p className="text-white/75 text-sm mt-1">{BUSINESS.hours.weekdays}.</p>
        <a href={BUSINESS.phoneHref} className="abc-btn abc-btn-accent w-full justify-center mt-4">
          <Phone className="w-4 h-4" /> Call {BUSINESS.phone}
        </a>
      </div>
    </aside>
  );
}

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const breadcrumbs = useMemo(
    () => (service ? [{ label: "Services", to: "/services" }, { label: service.name }] : []),
    [service]
  );
  if (!service) return <Navigate to="/services" replace />;

  const related = SERVICES.filter((s) => s.category === service.category && s.slug !== service.slug).slice(0, RELATED_SERVICE_COUNT);
  const content = getServiceContent(service.slug);
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.name,
    name: `${service.name} in the Baltimore & DC Metros`,
    description: content?.metaDescription || service.short,
    provider: { "@type": "Plumber", "@id": `${SITE_URL}/#business`, name: BUSINESS.name },
    areaServed: SERVICE_AREAS.map((a) => `${a.city}, ${a.state}`),
    url: `${SITE_URL}/services/${service.slug}`,
  };

  return (
    <Layout>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      <ServiceHero service={service} breadcrumbs={breadcrumbs} />

      <section className="py-10 md:py-14 abc-bg-pale">
        <div className="abc-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8 space-y-10">
            <ServiceOverview service={service} />
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-extrabold text-[#1A1D21]">Frequently asked questions</h2>
              <FaqList faqs={buildServiceFaqs(service)} testIdPrefix="service-faq" />
            </div>
          </div>
          <ServiceSidebar related={related} />
        </div>
      </section>

      <PreFooterCTA title={`Ready for ${service.name.toLowerCase()}?`} subtitle="Call now or request a free quote. We'll handle it cleanly." />
    </Layout>
  );
}
